import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, categorizeError, setActiveOrgId, getActiveOrgId } from "./api";

const AuthContext = createContext(null);

/**
 * AuthProvider — restaure la session utilisateur au démarrage puis expose
 * l'état d'authentification centralisé.
 *
 * ``status`` (source de vérité) :
 *   • ``loading``       — restauration en cours, aucun appel métier ne doit partir
 *   • ``authenticated`` — l'utilisateur et l'organisation active sont chargés
 *   • ``guest``         — 401 confirmé après relance / refresh (pas de session)
 *
 * ``authReady`` (alias booléen) = ``status !== "loading"``. Utile pour
 * conditionner un ``useEffect`` de chargement métier.
 */
export function AuthProvider({ children }) {
  const [state, setState] = useState({
    user: null,
    memberships: [],
    organizations: [],
    activeOrgId: null,
    activeRole: null,
    status: "loading",
  });
  const refreshingRef = useRef(false);

  const refresh = useCallback(async () => {
    if (refreshingRef.current) return null;
    refreshingRef.current = true;
    try {
      const { data } = await api.get("/auth/me");
      const orgs = data.organizations || [];
      const memberships = data.memberships || [];
      // Sélectionne l'organisation active : préférence stockée si toujours valide,
      // sinon default_organization_id, sinon la première membership.
      let active = getActiveOrgId();
      if (!active || !memberships.find((m) => m.organization_id === active)) {
        active = data.user?.default_organization_id || orgs[0]?.id || null;
      }
      setActiveOrgId(active);
      const activeMembership = memberships.find((m) => m.organization_id === active);
      setState({
        user: data.user,
        memberships,
        organizations: orgs,
        activeOrgId: active,
        activeRole: activeMembership?.role || null,
        status: "authenticated",
      });
      return data;
    } catch (e) {
      // Différencier "session vraiment expirée / absente" vs "problème réseau
      // ponctuel". Une erreur réseau/serveur ne doit PAS pousser l'utilisateur
      // vers /login — l'axios interceptor a déjà tenté 2 relances.
      const cat = categorizeError(e);
      if (cat.kind === "session_expired" || e.response?.status === 401) {
        setActiveOrgId(null);
        setState({
          user: null, memberships: [], organizations: [],
          activeOrgId: null, activeRole: null, status: "guest",
        });
      } else {
        // Erreur réseau/serveur : on reste en "loading" pour laisser une
        // seconde chance à l'utilisateur (bouton Réessayer via SmartLoader).
        setState((s) => ({ ...s, status: "loading", lastError: cat }));
      }
      return null;
    } finally {
      refreshingRef.current = false;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    try {
      await api.post("/auth/login", { email, password });
      const data = await refresh();
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: categorizeError(e).message };
    }
  }, [refresh]);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      await refresh();
      return { ok: true, email_verification_sent: !!data?.email_verification_sent };
    } catch (e) {
      return { ok: false, error: categorizeError(e).message };
    }
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    setActiveOrgId(null);
    setState({ user: null, memberships: [], organizations: [], activeOrgId: null, activeRole: null, status: "guest" });
  }, []);

  const switchOrg = useCallback((orgId) => {
    setActiveOrgId(orgId);
    setState((s) => {
      const activeMembership = s.memberships.find((m) => m.organization_id === orgId);
      return { ...s, activeOrgId: orgId, activeRole: activeMembership?.role || null };
    });
  }, []);

  const authReady = state.status !== "loading";
  const authenticated = state.status === "authenticated";

  return (
    <AuthContext.Provider
      value={{
        ...state,
        authReady,
        authenticated,
        login, register, logout, refresh, switchOrg,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
